import { useVirtualizer } from '@tanstack/react-virtual'
import FbButton from 'components/FbUI/FbButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import {
  FILTER_POPOVER_BODY,
  FILTER_POPOVER_CONTENT_BASE,
  FILTER_POPOVER_FOOTER,
} from 'components/Filters/fields/popoverClasses'
import { useEffect, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { ImRadioChecked, ImRadioUnchecked } from 'react-icons/im'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { useRegisterFilterKey } from '../components/FilterModal'
import { SelectFilterOption, SelectFilterProps } from './SelectFilter'

export function SingleSelectFilter(props: SelectFilterProps) {
  const { filterStore, filterKey, title, options } = props
  const {
    pendingValueFilters: pending,
    setPendingValueFilters: setPending,
    clearPendingValueFilter: clearPending,
    registerFilterTitle,
  } = filterStore()
  const selected = pending[filterKey]?.[0] ?? null
  const isActive = !!selected

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [internalSelected, setInternalSelected] =
    useState<SelectFilterOption | null>(selected ?? null)

  const filteredOptions = options
    ?.filter((o) => {
      const regex = new RegExp(search, 'gi')
      return regex.test(o.label)
    })
    .sort((a, b) => a.label.localeCompare(b.label))

  function handleInternalChange(option: SelectFilterOption | null) {
    setInternalSelected(option)
  }

  function clearFilters() {
    setInternalSelected(null)
    clearPending(filterKey)
  }

  function saveFilters() {
    setPending(
      props.filterKey,
      internalSelected
        ? [
            {
              value: internalSelected.value,
              label: internalSelected.label,
            },
          ]
        : []
    )
  }

  // Cancel raises this so the next close cycle skips the auto-save. Closing
  // any other way (outside-click, Escape, Apply button) still commits.
  const skipNextSaveRef = useRef(false)

  function handleOpenChange(open: boolean) {
    if (open) {
      // Rebuild internal state from the *applied* selection on each open
      // so a previously-cancelled session doesn't leave stale radio
      // selection lingering in `internalSelected`.
      setInternalSelected(selected ?? null)
    } else {
      if (skipNextSaveRef.current) {
        skipNextSaveRef.current = false
      } else {
        // Apply filters when the dropdown closes
        saveFilters()
      }
    }
    setOpen(open)
  }

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  useRegisterFilterKey(filterKey)

  useEffect(() => {
    setInternalSelected(selected)
  }, [selected])

  if (!options || !filteredOptions) return null

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <div className={'relative d-flex'}>
        <DropdownMenuTrigger asChild>
          <FilterButton open={open} title={title} isActive={isActive} />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isActive}
          open={open}
          onClick={() => clearFilters()}
        />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        // Mobile cap (FIR-7449): see popoverClasses.ts.
        className={cn(FILTER_POPOVER_CONTENT_BASE, 'w-[280px] lg:w-[260px]')}
        collisionPadding={12}
        sideOffset={6}
      >
        <div className={FILTER_POPOVER_BODY}>
          {/* Header: title + Reset (no bulk checkbox — single-select can only
            have one or zero selections). */}
          <div className="flex items-center justify-between gap-2 pb-2">
            <span className="text-sm font-semibold text-neutral-800 truncate">
              {title}
            </span>
            <button
              type="button"
              onClick={() => clearFilters()}
              disabled={!internalSelected}
              className={cn(
                'text-sm font-medium transition-colors',
                !internalSelected
                  ? 'text-neutral-400 cursor-not-allowed'
                  : 'text-brand-active hover:text-brand-bright'
              )}
            >
              Reset
            </button>
          </div>
          <hr className="border-0 border-t border-neutral-200 -mx-3 mb-2" />

          {!props.hideSearch && (
            <Form.Control
              type="text"
              onChange={(e) => {
                setSearch(e.target.value)
              }}
              placeholder="Search"
              className="!text-sm !border-neutral-300 !rounded-lg"
            />
          )}

          <ItemsList
            options={filteredOptions}
            onSelect={handleInternalChange}
            selected={internalSelected}
            column={filterKey}
          />
        </div>

        <div
          className={cn(
            FILTER_POPOVER_FOOTER,
            'mt-3 items-center justify-end gap-2'
          )}
        >
          <FbButton
            size={'sm'}
            variant="secondary"
            type="button"
            onClick={() => {
              // Skip auto-save so Cancel actually throws away the radio
              // selection instead of silently committing on close.
              skipNextSaveRef.current = true
              setOpen(false)
            }}
          >
            Cancel
          </FbButton>
          <FbButton
            size={'sm'}
            type="button"
            className={
              '!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100'
            }
            // Stays enabled when a filter is currently applied (`isActive`)
            // so the user can clear the radio selection and Apply the
            // empty state to drop the filter from the store.
            disabled={internalSelected == null && !isActive}
            onClick={() => {
              saveFilters()
              setOpen(false)
            }}
          >
            Apply
          </FbButton>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ItemsList(props: {
  options: SelectFilterOption[]
  selected: SelectFilterOption | null
  onSelect: (option: SelectFilterOption) => void
  column: string
}) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtual = useVirtualizer({
    count: props.options?.length ?? 1,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
  })

  return (
    <div
      className={'mt-2 w-full'}
      ref={parentRef}
      style={{
        height: `150px`,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          height: `${virtual.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtual.getVirtualItems().map((item) => {
          const o = props.options[item.index]
          const isSelected = props.selected?.value === o?.value
          if (!o) return null
          return (
            <div
              key={props.column + item.index}
              className={cn(
                'max-w-full cursor-pointer flex items-center py-2 px-2 gap-2 rounded-lg hover:bg-neutral-100 text-sm text-neutral-700 overflow-ellipsis whitespace-nowrap'
              )}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }}
              onClick={() => props.onSelect(o)}
            >
              <div className={'flex-shrink-0'}>
                {isSelected ? <ImRadioChecked /> : <ImRadioUnchecked />}
              </div>
              <div
                className={
                  'overflow-ellipsis overflow-hidden whitespace-nowrap'
                }
              >
                {o.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
